import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Filter, Info, RefreshCw } from 'lucide-react';
import { api, BatchEfficiencyRow, reportsApi } from '../api';

interface PartnerOption {
  mp_id: number;
  name: string;
}

const STATUS_OPTIONS = [
  { value: 'InProgress', label: 'In Progress' },
  { value: 'InPlacement', label: 'In Placement' },
  { value: 'PostPlacement', label: 'Post Placement' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Cancelled', label: 'Cancelled' },
];

const DEFAULT_STATUSES = ['InProgress', 'InPlacement', 'PostPlacement', 'Completed'];

const ReportsBatchEfficiency: React.FC = () => {
  const [partners, setPartners] = useState<PartnerOption[]>([]);
  const [rows, setRows] = useState<BatchEfficiencyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFormulaGuide, setShowFormulaGuide] = useState(false);

  const [filters, setFilters] = useState({
    partnerId: '',
    statuses: DEFAULT_STATUSES,
  });

  const [appliedFilters, setAppliedFilters] = useState({
    partnerId: undefined as number | undefined,
    statuses: DEFAULT_STATUSES,
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  useEffect(() => {
    if (!appliedFilters.partnerId) {
      setRows([]);
      return;
    }
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  const fetchPartners = async () => {
    try {
      const response = await api.get<{ success?: boolean; data?: PartnerOption[] }>('/partners');
      if (Array.isArray(response.data?.data)) {
        const data = response.data.data.slice().sort((a, b) => a.name.localeCompare(b.name));
        setPartners(data);
      }
    } catch (err) {
      console.error('Failed to load partners for report filters.', err);
    }
  };

  const fetchReport = async () => {
    if (!appliedFilters.partnerId) return;

    try {
      setLoading(true);
      setError(null);

      const params = {
        mpId: appliedFilters.partnerId,
        statuses: appliedFilters.statuses.join(','),
      };

      const response = await reportsApi.getBatchEfficiency(params);
      setRows(response.data);
    } catch (err: any) {
      console.error('Failed to fetch batch efficiency report.', err);
      const message = err?.response?.data?.message ?? 'Failed to fetch report data.';
      setError(message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const partnerId = filters.partnerId ? Number(filters.partnerId) : undefined;

    if (!partnerId) {
      setAppliedFilters({
        partnerId: undefined,
        statuses: filters.statuses,
      });
      return;
    }

    setAppliedFilters({
      partnerId,
      statuses: filters.statuses.length ? filters.statuses : DEFAULT_STATUSES,
    });
  };

  const handleReset = () => {
    setFilters({
      partnerId: '',
      statuses: DEFAULT_STATUSES,
    });
    setAppliedFilters({
      partnerId: undefined,
      statuses: DEFAULT_STATUSES,
    });
    setRows([]);
    setError(null);
  };

  const toggleStatus = (status: string) => {
    setFilters((prev) => {
      const exists = prev.statuses.includes(status);
      const nextStatuses = exists
        ? prev.statuses.filter((value) => value !== status)
        : [...prev.statuses, status];
      return { ...prev, statuses: nextStatuses };
    });
  };

  const handleExport = async () => {
    if (!appliedFilters.partnerId || exporting) return;

    try {
      setExporting(true);
      const blob = await reportsApi.exportBatchEfficiency({
        mpId: appliedFilters.partnerId,
        statuses: appliedFilters.statuses.join(','),
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      link.href = url;
      link.setAttribute('download', `batch-efficiency-report-${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export batch efficiency report.', err);
      setError('Failed to export report.');
    } finally {
      setExporting(false);
    }
  };

  const computedRows = useMemo(() => {
    const calcRate = (numerator: number, denominator: number) => {
      if (!denominator) return null;
      return (numerator / denominator) * 100;
    };

    const calcOverall = (rates: Array<number | null>) => {
      const hasAny = rates.some((rate) => rate != null);
      if (!hasAny) return null;
      const sum = rates.reduce((acc, rate) => acc + (rate ?? 0), 0);
      return sum / rates.length;
    };

    const calcGrade = (overall: number | null) => {
      if (overall == null) return '';
      if (overall < 55) return 'Below 55';
      if (overall <= 64) return 'D';
      if (overall <= 74) return 'C';
      if (overall <= 84) return 'B';
      if (overall <= 100) return 'A';
      return 'Invalid';
    };

    return rows.map((row) => {
      const launchpadRate = calcRate(row.actualLaunchpadSize, row.targetLaunchpadSize);
      const mobilizationRate = calcRate(row.actualBatchSize, row.targetBatchSize);
      const completionRate = calcRate(row.preAssessmentCompleted, row.preAssessmentAssigned);
      const certificationRate = calcRate(row.certifiedCount, row.preAssessmentCompleted);
      const placementRate = calcRate(row.placedCount, row.certifiedCount);
      const scoreChangeRate =
        row.avgPreScore && row.avgPreScore !== 0
          ? ((row.avgPostScore ?? 0) - row.avgPreScore) / row.avgPreScore * 100
          : null;

      const overallEfficiency = calcOverall([
        launchpadRate,
        mobilizationRate,
        completionRate,
        certificationRate,
        scoreChangeRate,
        placementRate,
      ]);

      return {
        ...row,
        launchpadRate,
        mobilizationRate,
        completionRate,
        certificationRate,
        placementRate,
        scoreChangeRate,
        overallEfficiency,
        efficiencyGrade: calcGrade(overallEfficiency),
      };
    });
  }, [rows]);

  const formatNumber = (value: number | null | undefined) =>
    value == null ? '' : value.toFixed(2);

  const formatPercent = (value: number | null | undefined) =>
    value == null ? '' : `${value.toFixed(2)}%`;

  const formulaItems = [
    {
      label: 'Launchpad Success Rate',
      formula: 'Actual Launchpad Size / Target Launchpad Size * 100',
    },
    {
      label: 'Mobilization Rate',
      formula: 'Actual Batch Size / Target Batch Size * 100',
    },
    {
      label: 'Completion Rate',
      formula: '# Completed / # Pre assessment * 100',
    },
    {
      label: 'Certification Rate',
      formula: '# Certified / # Completed * 100',
    },
    {
      label: 'Score Chanage rate',
      formula: '(Avg Post Score - Avg Pre Score) / Avg Pre Score * 100',
    },
    {
      label: 'Placement Rate',
      formula: '# Placed / # Certified * 100',
    },
    {
      label: 'Overall Partner / Batch Efficiency',
      formula: 'Average of six rate columns (missing rates treated as 0)',
    },
    {
      label: 'Efficiency Grade',
      formula: 'Below 55, D 55-64, C 65-74, B 75-84, A 85-100',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link
            to="/dashboard/reports"
            className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Reports
          </Link>
          <h1 className="mt-3 text-2xl font-bold text-gray-900">Batch Efficiency Report</h1>
          <p className="mt-1 text-sm text-gray-700">
            Track launchpad, assessment, certification, and placement efficiency for each batch.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={fetchReport}
            disabled={loading || !appliedFilters.partnerId}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleExport}
            disabled={!appliedFilters.partnerId || exporting}
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export XLSX'}
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <form className="grid grid-cols-1 gap-4 sm:grid-cols-4" onSubmit={handleFilterSubmit}>
          <div className="sm:col-span-1">
            <label htmlFor="partner-filter" className="form-label">
              Partner (required)
            </label>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                id="partner-filter"
                className="form-input pl-10"
                value={filters.partnerId}
                onChange={(event) => setFilters((prev) => ({ ...prev, partnerId: event.target.value }))}
                required
              >
                <option value="">Select partner</option>
                {partners.map((partner) => (
                  <option key={partner.mp_id} value={partner.mp_id}>
                    {partner.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="sm:col-span-2">
            <label className="form-label">Batch status</label>
            <div className="grid grid-cols-2 gap-2">
              {STATUS_OPTIONS.map((option) => (
                <label key={option.value} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    checked={filters.statuses.includes(option.value)}
                    onChange={() => toggleStatus(option.value)}
                  />
                  {option.label}
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Draft batches are always excluded. Cancelled batches show only when selected.
            </p>
          </div>

          <div className="sm:col-span-1 flex flex-col gap-2 sm:items-end sm:justify-end">
            <button type="submit" className="btn-secondary sm:w-auto">
              Apply filters
            </button>
            <button
              type="button"
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition sm:w-auto"
              onClick={handleReset}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        </form>

        {error && (
          <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 mt-4">
            {error}
          </div>
        )}
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <button
          type="button"
          className="w-full flex items-center justify-between px-6 py-4 border-b border-gray-200 text-left text-sm font-medium text-gray-700"
          onClick={() => setShowFormulaGuide((prev) => !prev)}
        >
          <span>Formula guide</span>
          <span className="text-primary-600">{showFormulaGuide ? 'Hide' : 'Show'}</span>
        </button>
        {showFormulaGuide && (
          <div className="px-6 pb-6 pt-2 text-sm text-gray-700">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {formulaItems.map((item) => (
                <div key={item.label} className="rounded-md border border-gray-100 bg-gray-50 p-3">
                  <div className="font-medium text-gray-900">{item.label}</div>
                  <div className="text-xs text-gray-600 mt-1">{item.formula}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Batch efficiency ({computedRows.length})
          </h2>
        </div>

        {!appliedFilters.partnerId ? (
          <div className="px-6 py-12 text-center text-sm text-gray-500">
            Select a partner to view the batch efficiency report.
          </div>
        ) : loading ? (
          <div className="px-6 py-12 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : computedRows.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3 text-left">Batch ID</th>
                  <th className="px-4 py-3 text-left">Partner Name</th>
                  <th className="px-4 py-3 text-left">Center Name</th>
                  <th className="px-4 py-3 text-left">Region/Place</th>
                  <th className="px-4 py-3 text-left">Batch Status</th>
                  <th className="px-4 py-3 text-right">Target Lauchpad Size</th>
                  <th className="px-4 py-3 text-right">Actual Launchpad size</th>
                  <th className="px-4 py-3 text-right">
                    <HeaderWithTooltip label="Launchpad Success" tooltip={formulaItems[0].formula} />
                  </th>
                  <th className="px-4 py-3 text-right">Target Batch Size</th>
                  <th className="px-4 py-3 text-right">Actual Batch Size</th>
                  <th className="px-4 py-3 text-right">
                    <HeaderWithTooltip label="Mobilization" tooltip={formulaItems[1].formula} />
                  </th>
                  <th className="px-4 py-3 text-right"># Pre assesment</th>
                  <th className="px-4 py-3 text-right"># Completed</th>
                  <th className="px-4 py-3 text-right">
                    <HeaderWithTooltip label="Completion" tooltip={formulaItems[2].formula} />
                  </th>
                  <th className="px-4 py-3 text-right"># Certified</th>
                  <th className="px-4 py-3 text-right">
                    <HeaderWithTooltip label="Certification" tooltip={formulaItems[3].formula} />
                  </th>
                  <th className="px-4 py-3 text-right">Avg Pre Score</th>
                  <th className="px-4 py-3 text-right">Avg Post Score</th>
                  <th className="px-4 py-3 text-right">
                    <HeaderWithTooltip label="Score Chanage rate" tooltip={formulaItems[4].formula} />
                  </th>
                  <th className="px-4 py-3 text-right"># Placed</th>
                  <th className="px-4 py-3 text-right">
                    <HeaderWithTooltip label="Placement" tooltip={formulaItems[5].formula} />
                  </th>
                  <th className="px-4 py-3 text-right">
                    <HeaderWithTooltip label="Overall Partner / Batch Efficiency" tooltip={formulaItems[6].formula} />
                  </th>
                  <th className="px-4 py-3 text-left">
                    <HeaderWithTooltip label="Efficiency Grade" tooltip={formulaItems[7].formula} />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {computedRows.map((row) => (
                  <tr key={row.batchId} className="hover:bg-gray-50 text-gray-700">
                    <td className="px-4 py-3 font-medium text-gray-900">{row.batchCode}</td>
                    <td className="px-4 py-3">{row.partnerName ?? '-'}</td>
                    <td className="px-4 py-3">{row.centerName ?? '-'}</td>
                    <td className="px-4 py-3">{row.city ?? '-'}</td>
                    <td className="px-4 py-3">{row.status}</td>
                    <td className="px-4 py-3 text-right">{formatNumber(row.targetLaunchpadSize)}</td>
                    <td className="px-4 py-3 text-right">{row.actualLaunchpadSize}</td>
                    <td className="px-4 py-3 text-right">{formatPercent(row.launchpadRate)}</td>
                    <td className="px-4 py-3 text-right">{formatNumber(row.targetBatchSize)}</td>
                    <td className="px-4 py-3 text-right">{row.actualBatchSize}</td>
                    <td className="px-4 py-3 text-right">{formatPercent(row.mobilizationRate)}</td>
                    <td className="px-4 py-3 text-right">{row.preAssessmentAssigned}</td>
                    <td className="px-4 py-3 text-right">{row.preAssessmentCompleted}</td>
                    <td className="px-4 py-3 text-right">{formatPercent(row.completionRate)}</td>
                    <td className="px-4 py-3 text-right">{row.certifiedCount}</td>
                    <td className="px-4 py-3 text-right">{formatPercent(row.certificationRate)}</td>
                    <td className="px-4 py-3 text-right">{formatNumber(row.avgPreScore)}</td>
                    <td className="px-4 py-3 text-right">{formatNumber(row.avgPostScore)}</td>
                    <td className="px-4 py-3 text-right">{formatPercent(row.scoreChangeRate)}</td>
                    <td className="px-4 py-3 text-right">{row.placedCount}</td>
                    <td className="px-4 py-3 text-right">{formatPercent(row.placementRate)}</td>
                    <td className="px-4 py-3 text-right">{formatPercent(row.overallEfficiency)}</td>
                    <td className={`px-4 py-3 font-semibold ${
                      row.efficiencyGrade === 'D'
                        ? 'text-red-600'
                        : row.efficiencyGrade
                          ? 'text-green-600'
                          : 'text-gray-400'
                    }`}>
                      {row.efficiencyGrade}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-12 text-center text-sm text-gray-500">
            No batches match the current filters.
          </div>
        )}
      </div>
    </div>
  );
};

const HeaderWithTooltip: React.FC<{ label: string; tooltip: string }> = ({ label, tooltip }) => (
  <div className="flex items-center gap-1">
    <span>{label}</span>
    <span className="group relative inline-flex">
      <Info className="h-3 w-3 text-gray-400" />
      <span className="absolute left-0 top-full z-10 mt-2 w-48 rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition group-hover:opacity-100">
        {tooltip}
      </span>
    </span>
  </div>
);

export default ReportsBatchEfficiency;
